import { getDangerousWasteRecordTableValue } from './dangerousWasteRecordColumns.js';

// Shown in place of a missing grouping value. A record with no value for a
// grouping column still belongs to a record group.
export const UNSPECIFIED_GROUPING_VALUE_LABEL = 'Ikke angivet';

const getGroupingColumnLabel = (column) => {
    const label = String(column?.display_name || '').trim();
    return label || String(column?.id || '');
};

/**
 * Groups dangerous waste records by the composite key formed by the grouping
 * column values. Two records belong to the same group when every grouping
 * column value matches.
 *
 * Like the records table, this reads a positional eval `values` list by index against
 * `allColumns` (the waste receiver's full column set, ignoring any columns list the record
 * carries) rather than by column id, so `allColumns` must be passed to resolve those values;
 * omitting it just falls back to legacy keyed evals.
 *
 * Returns groups ordered by record count descending. The counts of all groups
 * always sum to the number of records passed in.
 */
export function buildDangerousWasteRecordGroups(records, groupingColumns, allColumns = []) {
    const columns = (Array.isArray(groupingColumns) ? groupingColumns : [])
        .filter((column) => column && column.id);
    const recordList = Array.isArray(records) ? records : [];

    if (columns.length === 0 || recordList.length === 0) return [];

    const columnIndexById = new Map(
        (Array.isArray(allColumns) ? allColumns : []).map((column, index) => [column.id, index]),
    );
    const columnCount = Array.isArray(allColumns) ? allColumns.length : 0;

    const groupsByKey = new Map();

    recordList.forEach((record) => {
        const cells = columns.map((column) => ({
            id: column.id,
            label: getGroupingColumnLabel(column),
            value: getDangerousWasteRecordTableValue(
                record,
                column.id,
                columnIndexById.get(column.id),
                columnCount,
            ) || UNSPECIFIED_GROUPING_VALUE_LABEL,
        }));

        const key = JSON.stringify(cells.map((cell) => [cell.id, cell.value]));
        const existingGroup = groupsByKey.get(key);

        if (existingGroup) {
            existingGroup.count += 1;
        } else {
            groupsByKey.set(key, { key, cells, count: 1 });
        }
    });

    const total = recordList.length;

    return [...groupsByKey.values()]
        .sort((a, b) => b.count - a.count)
        .map((group) => ({ ...group, share: group.count / total }));
}
